import type { DataGridFilterOption, DataGridFilterOptionsLoader } from '@ibsys/kit';
import { queryClient } from '@/shared/api';
import { FILTER_OPTIONS_STALE_TIME, toFilterOptionsPage, toPersonFilterOption } from '@/shared/lib';
import { fetchUserById, fetchUsers } from './api';
import { usersKeys } from './hooks';

/**
 * Асинхронный источник опций фильтра DataGrid по пользователю системы: активные пользователи
 * страницами с поиском по имени на сервере. Значение опции — идентификатор пользователя.
 *
 * Ссылка на функцию модульная (стабильная) — кит держит по ней кеш подписей выбранных значений.
 * Отменой запроса владеет кеш, а не грид: запрос общий для всех, кто ждёт ту же страницу,
 * и оборвать его сигналом одного фильтра нельзя.
 */
export const loadUserOptions: DataGridFilterOptionsLoader = async ({ search, page, pageSize }) => {
  const offset = page * pageSize;
  const params = { isActive: true, name: search || undefined, limit: pageSize, offset };
  const { total, users } = await queryClient.fetchQuery({
    queryKey: usersKeys.filterOptions(params),
    queryFn: ({ signal }) => fetchUsers(params, signal),
    staleTime: FILTER_OPTIONS_STALE_TIME,
    retry: false,
  });
  return toFilterOptionsPage(users, total, offset, toPersonFilterOption);
};

/**
 * Подписи выбранных пользователей, которых нет ни в одной загруженной странице
 * (в том числе уже не активных): без догрузки в описании отбора и в карточке панели фильтров
 * стояли бы сырые идентификаторы.
 */
export const resolveUserOptions = async (values: Array<string | number>): Promise<DataGridFilterOption[]> => {
  const users = await Promise.all(
    values.map(Number).map((id) =>
      queryClient.fetchQuery({
        queryKey: usersKeys.detail(id),
        queryFn: ({ signal }) => fetchUserById(id, signal),
        staleTime: FILTER_OPTIONS_STALE_TIME,
        retry: false,
      }),
    ),
  );
  return users.map(toPersonFilterOption);
};
